import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Plus, HeadphonesIcon, Loader2, Search, CheckCircle, Clock, XCircle, Pencil, Trash2, ExternalLink } from "lucide-react";

interface SupportTicket {
  id: string;
  customerId: string | null;
  subject: string;
  description: string | null;
  category: string;
  priority: string;
  status: string;
  resolution: string | null;
  assignedTo: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function CustomerSupport() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<SupportTicket>>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    category: "OTHER",
    priority: "MEDIUM",
  });

  const { data: tickets, isLoading } = useQuery<SupportTicket[]>({
    queryKey: ["support-tickets"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/support/tickets");
      if (!response.ok) throw new Error("Failed to fetch tickets");
      return response.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest("POST", "/api/support/tickets", data);
      if (!response.ok) throw new Error("Failed to create ticket");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      toast({ title: "Success", description: "Ticket created successfully" });
      setIsOpen(false);
      setFormData({ subject: "", description: "", category: "OTHER", priority: "MEDIUM" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { id: string; updates: Partial<SupportTicket> }) => {
      const response = await apiRequest("PATCH", `/api/support/tickets/${data.id}`, data.updates);
      if (!response.ok) throw new Error("Failed to update ticket");
      return response.json();
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      setSelectedTicket(updated);
      setIsEditing(false);
      toast({ title: "Success", description: "Ticket updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update ticket", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/support/tickets/${id}`);
      if (!response.ok) throw new Error("Failed to delete ticket");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      setSelectedTicket(null);
      setDeleteConfirmOpen(false);
      toast({ title: "Success", description: "Ticket deleted successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete ticket", variant: "destructive" });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async (data: { id: string; resolution: string }) => {
      const response = await apiRequest("POST", `/api/support/tickets/${data.id}/resolve`, { resolution: data.resolution });
      if (!response.ok) throw new Error("Failed to resolve ticket");
      return response.json();
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      setSelectedTicket(updated);
      setResolveDialogOpen(false);
      setResolutionNotes("");
      toast({ title: "Success", description: "Ticket resolved" });
    },
  });

  const filteredTickets = tickets?.filter((t) =>
    t.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.status.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      URGENT: "destructive",
      HIGH: "destructive",
      MEDIUM: "secondary",
      LOW: "outline",
    };
    const colors: Record<string, string> = {
      URGENT: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
      HIGH: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
      MEDIUM: "",
      LOW: "",
    };
    return <Badge variant={variants[priority] || "secondary"} className={colors[priority]}>{priority}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      RESOLVED: "default",
      IN_PROGRESS: "secondary",
      OPEN: "outline",
      CLOSED: "secondary",
    };
    const colors: Record<string, string> = {
      RESOLVED: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      IN_PROGRESS: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      OPEN: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
      CLOSED: "",
    };
    return <Badge variant={variants[status] || "secondary"} className={colors[status]}>{status.replace("_", " ")}</Badge>;
  };

  const handleEdit = () => {
    if (selectedTicket) {
      setEditForm({
        subject: selectedTicket.subject,
        description: selectedTicket.description || "",
        category: selectedTicket.category,
        priority: selectedTicket.priority,
        status: selectedTicket.status,
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    if (selectedTicket && editForm) {
      updateMutation.mutate({ id: selectedTicket.id, updates: editForm });
    }
  };

  const handleDelete = () => {
    if (selectedTicket) {
      deleteMutation.mutate(selectedTicket.id);
    }
  };

  const handleResolve = () => {
    if (selectedTicket && resolutionNotes) {
      resolveMutation.mutate({ id: selectedTicket.id, resolution: resolutionNotes });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Customer Support</h1>
          <p className="text-muted-foreground">Manage support tickets</p>
        </div>
        <div className="flex items-center gap-3">
          <a href="https://vedalogic.betteruptime.com/" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm">
              <span className="h-2 w-2 rounded-full bg-emerald-500 mr-2" />
              System Status
              <ExternalLink className="h-3 w-3 ml-1.5" />
            </Button>
          </a>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button><Plus className="mr-2 h-4 w-4" />Create Ticket</Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Support Ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(formData); }} className="space-y-4">
              <div className="space-y-2">
                <Label>Subject *</Label>
                <Input value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CARD_ISSUE">Card Issue</SelectItem>
                      <SelectItem value="TRANSACTION">Transaction</SelectItem>
                      <SelectItem value="KYC">KYC</SelectItem>
                      <SelectItem value="BILLING">Billing</SelectItem>
                      <SelectItem value="OTHER">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Low</SelectItem>
                      <SelectItem value="MEDIUM">Medium</SelectItem>
                      <SelectItem value="HIGH">High</SelectItem>
                      <SelectItem value="URGENT">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Ticket
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Support Tickets</CardTitle>
              <CardDescription>{filteredTickets?.length ?? 0} ticket(s) found</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : filteredTickets?.length === 0 ? (
            <div className="text-center py-12">
              <HeadphonesIcon className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No tickets</h3>
              <p className="text-muted-foreground">No support tickets yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTickets?.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <TableCell className="font-medium">{ticket.subject}</TableCell>
                    <TableCell><Badge variant="outline">{ticket.category.replace("_", " ")}</Badge></TableCell>
                    <TableCell>{getPriorityBadge(ticket.priority)}</TableCell>
                    <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                    <TableCell>{new Date(ticket.createdAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Ticket Detail Sheet */}
      <Sheet
        open={!!selectedTicket}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTicket(null);
            setIsEditing(false);
          }
        }}
      >
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between pr-8">
              <span>{isEditing ? "Edit Ticket" : "Ticket Details"}</span>
              {!isEditing && selectedTicket?.status !== "RESOLVED" && selectedTicket?.status !== "CLOSED" && (
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={handleEdit}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeleteConfirmOpen(true)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </SheetTitle>
            <SheetDescription>#{selectedTicket?.id.slice(0, 8)}</SheetDescription>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-200px)] mt-6">
            {selectedTicket && (
              <div className="space-y-6 pr-4">
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject</Label>
                      <Input
                        id="subject"
                        value={editForm.subject || ""}
                        onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={editForm.description || ""}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        rows={4}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="category">Category</Label>
                        <Select
                          value={editForm.category}
                          onValueChange={(value) => setEditForm({ ...editForm, category: value })}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="CARD_ISSUE">Card Issue</SelectItem>
                            <SelectItem value="TRANSACTION">Transaction</SelectItem>
                            <SelectItem value="KYC">KYC</SelectItem>
                            <SelectItem value="BILLING">Billing</SelectItem>
                            <SelectItem value="OTHER">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="priority">Priority</Label>
                        <Select
                          value={editForm.priority}
                          onValueChange={(value) => setEditForm({ ...editForm, priority: value })}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="LOW">Low</SelectItem>
                            <SelectItem value="MEDIUM">Medium</SelectItem>
                            <SelectItem value="HIGH">High</SelectItem>
                            <SelectItem value="URGENT">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={editForm.status}
                        onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="OPEN">Open</SelectItem>
                          <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                          <SelectItem value="RESOLVED">Resolved</SelectItem>
                          <SelectItem value="CLOSED">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-muted-foreground">Ticket Information</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2 space-y-1">
                          <p className="text-sm text-muted-foreground">Subject</p>
                          <p className="font-medium">{selectedTicket.subject}</p>
                        </div>
                        <div className="col-span-2 space-y-1">
                          <p className="text-sm text-muted-foreground">Description</p>
                          <p className="font-medium">{selectedTicket.description || "No description provided"}</p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-muted-foreground">Classification</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Category</p>
                          <Badge variant="outline">{selectedTicket.category.replace("_", " ")}</Badge>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Priority</p>
                          {getPriorityBadge(selectedTicket.priority)}
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Status</p>
                          {getStatusBadge(selectedTicket.status)}
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Assigned To</p>
                          <p className="font-medium">{selectedTicket.assignedTo || "Unassigned"}</p>
                        </div>
                      </div>
                    </div>

                    {selectedTicket.resolution && (
                      <>
                        <Separator />
                        <div className="space-y-4">
                          <h4 className="text-sm font-semibold text-muted-foreground">Resolution</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2 space-y-1">
                              <p className="text-sm text-muted-foreground">Resolution Notes</p>
                              <p className="font-medium">{selectedTicket.resolution}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm text-muted-foreground">Resolved At</p>
                              <p className="font-medium">{selectedTicket.resolvedAt ? new Date(selectedTicket.resolvedAt).toLocaleString() : "-"}</p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    <Separator />

                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-muted-foreground">Timestamps</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Created</p>
                          <p className="font-medium">{new Date(selectedTicket.createdAt).toLocaleString()}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Updated</p>
                          <p className="font-medium">{new Date(selectedTicket.updatedAt).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </ScrollArea>
          <SheetFooter className="mt-4">
            {isEditing ? (
              <div className="flex justify-end gap-2 w-full">
                <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            ) : (selectedTicket?.status === "OPEN" || selectedTicket?.status === "IN_PROGRESS") && (
              <div className="flex justify-end gap-2 w-full">
                {selectedTicket?.status === "OPEN" && (
                  <Button
                    variant="outline"
                    onClick={() => updateMutation.mutate({ id: selectedTicket.id, updates: { status: "IN_PROGRESS" } })}
                    disabled={updateMutation.isPending}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Start Working
                  </Button>
                )}
                <Button onClick={() => setResolveDialogOpen(true)}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Resolve Ticket
                </Button>
              </div>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Resolve Ticket Dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Ticket</DialogTitle>
            <DialogDescription>Enter resolution notes for this ticket.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Resolution Notes</Label>
              <Textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Describe how the issue was resolved..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleResolve} disabled={resolveMutation.isPending || !resolutionNotes}>
              {resolveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ticket</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this ticket? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              <XCircle className="h-4 w-4 mr-2" />
              Delete Ticket
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
